/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-globals */
import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Icon,
  LinearProgress, Paper, Snackbar, SnackbarCloseReason, Typography, alpha, useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  IVFormErrors, VForm, VSwitch, VTextFieldData, VFieldMoney, useVForm,
  SeletorFormasPagamento, IFormaPagamentoSelecionada, OPERACOES_PAGAMENTO_CONTASPAGAR,
} from '../../../shared/forms';
import { FullScreenLoading } from '../../../shared/components';
import { ContasPagarService, IListagemContasPagar } from '../../../shared/services/api/Financeiro/ContasPagar';
import { ContaBancariaService } from '../../../shared/services/api/cadastros/contaBancaria/ContaBancariaService';
import { SessaoCaixaService, ISessaoCaixaAtiva } from '../../../shared/services/api/Financeiro/SessaoCaixa';
import * as yup from 'yup';

// Conta "Cofre" — mesmo idconta fixo em todas as empresas, ver
// CAIXA_TURNO_COFRE_IDCONTA (backend, GetTurnoStatus.ts).
const CONTA_COFRE_IDCONTA = 'eb145f4b-c12a-482f-95cd-93db233e74ad';

export interface ConfirmationDialogRawProps {
  value: string;
  open: boolean;
  dados?: IListagemContasPagar[];
  vselecionado: number;
  onClose: (value?: string) => void;
  ontotal: (value?: number) => void;
  onDados: (dados?: IListagemContasPagar[]) => void;
}

interface IFormData {
  total: number;
  databaixa: Date;
  tipocaixa: boolean;
}

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  total: yup.number().required(),
  databaixa: yup.date().required(),
  tipocaixa: yup.boolean().required(),
});

const formatarMoeda = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Mesmo modelo de dialog da baixa de Contas a Receber (ver DialogBaixa.tsx
// em ContasReceber): resumo lateral, seletor de formas de pagamento com
// lista de adicionados e loading em tela cheia. Aqui o dinheiro sai, não
// entra, então cada forma escolhe sua própria origem: Dinheiro sai da
// sessão de caixa aberta ou do Cofre (toggle fechado, ver `origemDinheiro`
// em SeletorFormasPagamento), PIX/Transferência saem de uma conta bancária
// escolhida na hora. Cartão nunca fez sentido pra pagar fornecedor, por
// isso fica de fora.
export const DialoBaixa: React.FC<ConfirmationDialogRawProps> = (props: ConfirmationDialogRawProps) => {
  const navigate = useNavigate();
  const { onClose, value: valueProp, open, ...other } = props;
  const theme = useTheme();
  const [value, setValue] = useState(valueProp);
  const { formRef, save, } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const [showFullScreenLoading, setShowFullScreenLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Realizando baixa...');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const requestInProgress = useRef(false);
  const [id, setId] = useState('');
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const [formasPagamentoSelecionadas, setFormasPagamentoSelecionadas] = useState<IFormaPagamentoSelecionada[]>([]);
  // Origem do Dinheiro: sessão de caixa aberta do usuário e/ou Cofre — só
  // oferece cada opção se ela existir (sessão pode não estar aberta; Cofre
  // pode não estar cadastrado nesse tenant).
  const [sessaoAtiva, setSessaoAtiva] = useState<ISessaoCaixaAtiva | null>(null);
  const [cofreLabel, setCofreLabel] = useState<string | null>(null);

  const somaFormas = formasPagamentoSelecionadas.reduce((acc, f) => acc + f.valor, 0);
  const valorPago = somaFormas;
  const restante = Math.max(0, Number((props.vselecionado - valorPago).toFixed(2)));
  const percentualPago = props.vselecionado > 0 ? Math.min(100, (valorPago / props.vselecionado) * 100) : 0;
  const parceiroNome = props.dados?.[0]?.nome ?? '';

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
    if (tms === 'success') {
      onClose('true');
      navigate('/contaspagar');
    }
  };

  useEffect(() => {
    if (props.dados && typeof props.dados === 'object') {
      formRef.current?.setFieldValue('total', props.vselecionado);
    }
  }, [id, formRef]);

  const simulateProgress = () => {
    setLoadingProgress(0);
    const interval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 400);
    return () => clearInterval(interval);
  };

  const handleSave = (dados: IFormData) => {
    if (requestInProgress.current) return;
    requestInProgress.current = true;
    let progressCleanup: (() => void) | null = null;

    formValidationSchema.
      validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        if (formasPagamentoSelecionadas.length === 0) {
          requestInProgress.current = false;
          setTms('error');
          setMsg('Selecione ao menos uma forma de pagamento');
          setOpenmsg(true);
          return;
        }
        if (somaFormas > props.vselecionado + 0.01) {
          requestInProgress.current = false;
          setTms('error');
          setMsg(`Soma das formas de pagamento (${somaFormas.toFixed(2)}) não pode ultrapassar o valor selecionado (${props.vselecionado.toFixed(2)})`);
          setOpenmsg(true);
          return;
        }
        if (!props.dados || typeof props.dados !== 'object') {
          requestInProgress.current = false;
          return;
        }

        // Cada forma já carrega sua própria conta de origem (ver
        // `origemDinheiro`/`formasComContaObrigatoria` no seletor) — o
        // `idconta` enviado aqui só é usado no fallback do provider (não
        // deveria ser necessário, mas evita quebrar se algum item vier sem
        // forma).
        const idcontaFallback = formasPagamentoSelecionadas[0]?.idcontadestino || sessaoAtiva?.idconta || '';

        const dadosComFormasPagamento = {
          vpagar: valorPago,
          vselecionado: props.vselecionado,
          idconta: idcontaFallback,
          databaixa: dadosValidados.databaixa,
          numeroch: '',
          tipocaixa: dadosValidados.tipocaixa,
          formasPagamento: formasPagamentoSelecionadas.map(f => ({
            idoperacao: f.idoperacao, valor: f.valor, categoria: f.nome, idcontadestino: f.idcontadestino,
          })),
        };

        setIsLoading(true);
        setShowFullScreenLoading(true);
        setLoadingMessage('Processando baixa...');
        progressCleanup = simulateProgress();
        setLoadingProgress(40);

        ContasPagarService
          .Baixa(dadosComFormasPagamento, props.dados)
          .then((result) => {
            setLoadingProgress(100);
            setTimeout(() => {
              setIsLoading(false);
              setShowFullScreenLoading(false);
              // Libera a trava assim que a baixa termina (sucesso ou erro) —
              // sem isso o botão Finalizar ficava travado na próxima baixa
              // (mesmo bug corrigido em ContasReceber/DialogBaixa.tsx).
              requestInProgress.current = false;
              if (progressCleanup) progressCleanup();

              if (result instanceof Error) {
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
                return;
              }
              setValue('0');
              setTms('success');
              setMsg('Baixa efetuada com sucesso!');
              setOpenmsg(true);
            }, 300);
          });
      })
      .catch((errors: yup.ValidationError) => {
        requestInProgress.current = false;
        setIsLoading(false);
        setShowFullScreenLoading(false);
        if (progressCleanup) progressCleanup();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  const handleCancel = () => {
    if (showFullScreenLoading) return;
    setValue('0');
    onClose('false');
  };

  useEffect(() => {
    if (!open) {
      setValue(valueProp);
    } else {
      setId(valueProp);
      setFormasPagamentoSelecionadas([]);
    }
  }, [valueProp, open]);

  useEffect(() => {
    if (!open) return;
    let cancelado = false;
    SessaoCaixaService.getAtiva().then((resultado) => {
      if (cancelado) return;
      setSessaoAtiva(resultado instanceof Error ? null : resultado);
    });
    ContaBancariaService.getAllList('', CONTA_COFRE_IDCONTA).then((resultado) => {
      if (cancelado) return;
      const conta = !(resultado instanceof Error) ? resultado.data[0] : undefined;
      setCofreLabel(conta ? (conta.numeroconta || conta.titular || 'Cofre') : null);
    });
    return () => { cancelado = true; };
  }, [open]);

  // F12 confirma, Esc cancela — Enter só confirma fora de campos de texto,
  // mesmo padrão de ContasReceber/DialogBaixa.tsx.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (showFullScreenLoading) return;
      if (event.key === 'F12') {
        event.preventDefault();
        save();
        return;
      }
      if (event.key === 'Escape') {
        handleCancel();
        return;
      }
      const alvo = event.target as HTMLElement | null;
      const digitando = alvo && ['INPUT', 'TEXTAREA'].includes(alvo.tagName);
      if (event.key === 'Enter' && !digitando) {
        event.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showFullScreenLoading]);

  return (
    <>
      {showFullScreenLoading && (
        <FullScreenLoading message={loadingMessage} progress={loadingProgress} />
      )}
      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <Icon color="primary">payments</Icon>
          <Typography fontWeight={700} fontSize="1.1rem">
            Baixar contas a pagar
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2.5 }}>
          <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 2000} onClose={handleCloseMsg}>
              <Alert
                onClose={handleCloseMsg}
                severity={tms}
                variant="filled"
                sx={{ width: '100%' }}
              >
                {msg}
              </Alert>
            </Snackbar>
            {isLoading && (
              <LinearProgress variant='indeterminate' sx={{ mb: 2 }} />
            )}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={6} md={4}>
                <VTextFieldData
                  fullWidth
                  size='small'
                  name='databaixa'
                  label='Data Pagamento'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <VFieldMoney
                  fullWidth
                  size='small'
                  name='total'
                  label='Total'
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <VSwitch
                  name='tipocaixa'
                  label='Juntar Lançamentos'
                  fdisabled={isLoading}
                  aliamento='start'
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Formas de pagamento
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
                <SeletorFormasPagamento
                  key={open ? 'aberto' : 'fechado'}
                  total={props.vselecionado}
                  formas={formasPagamentoSelecionadas}
                  onChange={setFormasPagamentoSelecionadas}
                  disabled={isLoading}
                  operacoes={OPERACOES_PAGAMENTO_CONTASPAGAR}
                  habilitarAtalhosTeclado
                  capturarDetalhesPagamento
                  ocultarTipoPix
                  formasComContaObrigatoria={['PIX', 'Transferência']}
                  labelContaBancaria="Conta bancária de origem"
                  origemDinheiro={{
                    sessao: sessaoAtiva ? { idconta: sessaoAtiva.idconta, label: sessaoAtiva.nomeConta } : null,
                    cofre: cofreLabel ? { idconta: CONTA_COFRE_IDCONTA, label: cofreLabel } : null,
                  }}
                />
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                  width: { xs: '100%', lg: 300 },
                  flexShrink: 0,
                }}
              >
                <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
                  Resumo da baixa
                </Typography>

                <Typography variant="caption" color="text.secondary">Fornecedor</Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5, lineHeight: 1.2 }}>
                  {parceiroNome}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {(props.dados || []).length} conta{(props.dados || []).length === 1 ? '' : 's'} selecionada{(props.dados || []).length === 1 ? '' : 's'}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Total selecionado</Typography>
                  <Typography variant="body1" fontWeight={700}>
                    {formatarMoeda(props.vselecionado)}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Valor pago</Typography>
                  <Typography variant="h6" fontWeight={800} color="primary.main">
                    {formatarMoeda(somaFormas)}
                  </Typography>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={percentualPago}
                  color={percentualPago >= 100 ? 'success' : 'primary'}
                  sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {percentualPago.toFixed(0)}%
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Restante</Typography>
                  <Typography variant="body1" fontWeight={700} color={restante > 0.001 ? 'warning.main' : 'success.main'}>
                    {formatarMoeda(restante)}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </VForm>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant='text' color='inherit' onClick={handleCancel} disabled={showFullScreenLoading}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            startIcon={<Icon>check</Icon>}
            onClick={save}
            disabled={isLoading || requestInProgress.current}
          >
            {isLoading ? 'Baixando...' : 'Confirmar Baixa (F12)'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
